import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import Editor from 'react-simple-code-editor'
import { highlight, languages } from 'prismjs'
import { getHotKeyHint } from 'keyux'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Modal, ModalContent, ModalFooter, ModalProps, ModalTitle, cn, formHotkeysMap } from '@dashboard/ui'
import { dashboardClient } from '@/api'
import { CardTemplateCreate, CardType } from '@/api/dashboard'
import { useGenerateCompanyPath } from '@/libs'
import { cardTemplatesQueryOpts } from '../api/cardTemplatesQueryOpts'
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism.css'

interface CardTemplateCreateModalProps extends ModalProps {
  type: CardType
}

export const CardTemplateCreateModal = ({ type, onClose, ...rest }: CardTemplateCreateModalProps) => {
  const ql = useQueryClient()
  const { companyId, gameId } = useGenerateCompanyPath()

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
    control,
    watch,
  } = useForm({
    defaultValues: {
      type,
      json: '',
    },
  })

  const { mutateAsync: createCardTemplate } = useMutation({
    mutationFn: (data: CardTemplateCreate) => dashboardClient.v1.createCardTemplate(companyId, gameId, data),
    onSuccess: async () => {
      await ql.refetchQueries({ queryKey: cardTemplatesQueryOpts(companyId, gameId).queryKey })
    },
  })

  const json = watch('json')

  const isValid = useMemo(() => {
    // TODO add zod validation
    try {
      json && JSON.parse(json)
    } catch (error) {
      return false
    }
    return true
  }, [json])

  const onSubmit = handleSubmit(async data => {
    try {
      await createCardTemplate({
        name: type,
        card_type: type,
        settings: JSON.parse(data.json),
      })
      toast.success('Card template created')
      onClose()
    } catch (_error) {
      toast.error('Failed to create card template')
    }
  })

  return (
    <Modal onClose={onClose} {...rest}>
      <form onSubmit={onSubmit}>
        <ModalContent>
          <ModalTitle
            subtitle={
              <>
                type: <span className="font-semibold text-text-primary underline">{type}</span>
              </>
            }
            onClose={onClose}
          >
            Edit card template
          </ModalTitle>
          <Controller
            control={control}
            name="json"
            render={({ field }) => (
              <Editor
                className={cn(
                  'rounded-md border border-border-primary transition-colors',
                  !isValid && 'border-border-error bg-fg-error-tertiary',
                )}
                value={field.value}
                onValueChange={field.onChange}
                highlight={code => highlight(code, languages.json, 'json')}
                placeholder="Enter JSON"
                padding={10}
                style={{
                  fontFamily: '"Fira code", "Fira Mono", monospace',
                  lineHeight: 1.25,
                  fontSize: 14,
                }}
              />
            )}
          />
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" color="secondary" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </Button>
          <Button
            aria-keyshortcuts={formHotkeysMap.submit}
            loading={isSubmitting}
            disabled={!isDirty || !isValid}
            type="submit"
          >
            <span>Create</span>
            <kbd>{getHotKeyHint(window, formHotkeysMap.submit)}</kbd>
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
